"use client";

import { useState, useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Plus } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import RichTextEditor from "@/components/admin/venues/rich-text-editor";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { AVAILABLE_LANGUAGES } from "@/lib/constants/languages";
import AddOnMediaUpload from "@/components/admin/add-ons/add-on-media-upload";
import { formatNumber } from "@/lib/utils";
import { getCurrencySymbol } from "@/lib/utils/currency";
import { STATUS_IDS } from "@/lib/constants/statuses";
import { AccountingArticleMapping } from "@/components/admin/shared/accounting-article-mapping";
import { ItemFaqsTab } from "@/components/admin/faqs/item-faqs-tab";
import { MAX_ADDON_DESCRIPTION_WORDS } from "@/lib/constants/addon-types";

const countWords = (value: string) =>
  value.trim().split(/\s+/).filter(Boolean).length;

const descriptionSchema = z
  .string()
  .optional()
  .refine(
    (value) =>
      !value || countWords(value) <= MAX_ADDON_DESCRIPTION_WORDS,
    {
      message: `Description must be ${MAX_ADDON_DESCRIPTION_WORDS} words or fewer`,
    },
  );

const formSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  description: descriptionSchema,
  add_on_type: z.enum(["food", "drink", "service", "equipment", "other"]),
  customer_type: z.enum(["corporate", "private"]).optional(),
  price_type_id: z.number().optional(),
  price: z.number().min(0, "Price must be non-negative"),
  vat_percentage: z.number().min(0).max(100, "VAT must be between 0 and 100"),
  status: z.enum(["available", "unavailable"]),
  order: z.number().min(0).optional(),
  is_on_road: z.boolean().optional(),
  is_catered: z.boolean().optional(),
  // Customer cutoffs (lib/booking/add-on-cutoffs.ts): days when catered;
  // otherwise the show rule set's participant cutoff (cutoff_hours saved null).
  catered_add_cutoff_days: z.coerce.number().int().min(0).optional(),
  catered_increase_cutoff_days: z.coerce.number().int().min(0).optional(),
  catered_decrease_cutoff_days: z.coerce.number().int().min(0).optional(),
  cutoff_hours: z
    .union([z.literal(""), z.coerce.number().int().min(0)])
    .nullable()
    .optional(),
  special_questions: z.string().optional(),
  translations: z
    .record(
      z.string(),
      z.object({
        name: z.string().optional(),
        description: descriptionSchema,
        special_questions: z.string().optional(),
      }),
    )
    .optional(),
  media_urls: z.array(z.string()).optional(),
});

type FormData = z.infer<typeof formSchema>;

interface AddOn {
  id: number;
  venue_id: number;
  name: string;
  description?: string;
  add_on_type: string;
  price_type_id?: number;
  customer_type?: string;
  customer_type_id?: number;
  pricing_id: number;
  price: number;
  vat_percentage: number;
  status?: string;
  status_id: number;
  article_code?: string | null;
  is_on_road?: boolean;
  is_catered?: boolean;
  catered_add_cutoff_days?: number;
  catered_increase_cutoff_days?: number;
  catered_decrease_cutoff_days?: number;
  cutoff_hours?: number | null;
  special_questions?: string;
  have_question?: boolean;
  order?: number;
  translations?: {
    [key: string]: {
      name?: string;
      description?: string;
      special_questions?: string;
    };
  };
  media_urls?: string[];
  created_at: string;
  updated_at: string;
}

interface EditAddOnFormProps {
  venueId: number;
  addon: AddOn;
  venueLanguages?: string[];
  venueCurrency?: string;
  venueAccountingId?: number | null;
  userRoleId?: number;
}

interface PriceRecord {
  id: string;
  price: number;
  vatPercentage: number;
  startDate: string;
  endDate: string | null;
  status: "active" | "inactive";
  createdDate: string;
}

export function EditAddOnForm({
  venueId,
  addon,
  venueLanguages = ["en", "sv"],
  venueCurrency = "SEK",
  venueAccountingId,
  userRoleId,
}: EditAddOnFormProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [isDataLoading, setIsDataLoading] = useState(true); // Initial data loading state
  const [generalLoading, setGeneralLoading] = useState(false); // For saving general settings
  const [contentLoading, setContentLoading] = useState(false); // For saving content updates
  const [mediaItems, setMediaItems] = useState<
    Array<{
      id?: string | number;
      url: string;
      type: "image";
      is_primary: boolean;
      lang: string;
      order?: number;
    }>
  >([]);
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [newPriceData, setNewPriceData] = useState({
    price: "",
    vatPercentage: "25",
  });
  const [priceTypes, setPriceTypes] = useState<
    Array<{ id: number; type: string }>
  >([]);
  const [isAddingPrice, setIsAddingPrice] = useState(false);

  const [priceHistory, setPriceHistory] = useState<PriceRecord[]>([]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: addon.name,
      description: addon.description || "",
      add_on_type: addon.add_on_type as any,
      customer_type:
        (addon.customer_type as "corporate" | "private" | undefined) ||
        "corporate",
      price_type_id: addon.price_type_id || 1, // Default to 'fixed' (id: 1)
      price: addon.price,
      vat_percentage: addon.vat_percentage,
      status: (addon.status as "available" | "unavailable") || "available",
      order: addon.order || 0,
      is_on_road: addon.is_on_road ?? false,
      is_catered: addon.is_catered ?? false,
      catered_add_cutoff_days: addon.catered_add_cutoff_days ?? 7,
      catered_increase_cutoff_days: addon.catered_increase_cutoff_days ?? 3,
      catered_decrease_cutoff_days: addon.catered_decrease_cutoff_days ?? 1,
      cutoff_hours: addon.cutoff_hours ?? null,
      special_questions: addon.special_questions || "",
      translations: addon.translations || {},
      media_urls: addon.media_urls || [],
    },
  });

  const saveGeneralSettings = async () => {
    setGeneralLoading(true);
    try {
      const formData = form.getValues();

      // Validate required fields for general settings
      const errors = await form.trigger([
        "customer_type",
        "add_on_type",
        "status",
        "order",
      ]);
      if (!errors) {
        toast.error("Please fill in all required fields");
        setGeneralLoading(false);
        return;
      }

      const response = await fetch(
        `/api/venues/${venueId}/add-ons/${addon.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            // Include name to satisfy API validation, but it won't be updated
            name: formData.name || addon.name,
            customer_type: formData.customer_type,
            add_on_type: formData.add_on_type,
            price_type_id: formData.price_type_id,
            status: formData.status,
            order: formData.order,
            is_on_road: formData.is_on_road,
            is_catered: formData.is_catered,
            catered_add_cutoff_days: formData.catered_add_cutoff_days,
            catered_increase_cutoff_days: formData.catered_increase_cutoff_days,
            catered_decrease_cutoff_days: formData.catered_decrease_cutoff_days,
            // Non-catered add-ons follow the show's participant cutoff.
            cutoff_hours: null,
            update_section: "general", // Flag to indicate partial update
          }),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update general settings");
      }

      toast.success("General settings updated successfully");
      router.refresh();
    } catch (error: any) {
      toast.error(error.message || "Failed to update general settings");
    } finally {
      setGeneralLoading(false);
    }
  };

  const saveContent = async () => {
    setContentLoading(true);
    try {
      const formData = form.getValues();

      // Validate required fields for content
      const errors = await form.trigger(["name"]);
      if (!errors) {
        toast.error("Please fill in all required fields");
        setContentLoading(false);
        return;
      }

      const response = await fetch(
        `/api/venues/${venueId}/add-ons/${addon.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: formData.name,
            description: formData.description,
            special_questions: formData.special_questions,
            add_on_type: formData.add_on_type || addon.add_on_type, // Include to satisfy API
            translations: formData.translations,
            have_question: showSpecialQuestions, // Set true/false based on single checkbox state
            update_section: "content", // Flag to indicate partial update
          }),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update content");
      }

      toast.success("Content updated successfully");
      router.refresh();
    } catch (error: any) {
      toast.error(error.message || "Failed to update content");
    } finally {
      setContentLoading(false);
    }
  };

  const [activeTab, setActiveTab] = useState<
    "general" | "pricing" | "content" | "faqs"
  >("general");
  const [activeLanguageTab, setActiveLanguageTab] = useState<string>(
    venueLanguages[0] || "en",
  );
  const [showSpecialQuestions, setShowSpecialQuestions] = useState<boolean>(false);

  const fetchMedia = async () => {
    try {
      const mediaResponse = await fetch(
        `/api/venues/${venueId}/add-ons/${addon.id}/media`,
      );
      if (mediaResponse.ok) {
        const { data } = await mediaResponse.json();
        setMediaItems(data || []);
      } else {
        console.error("Failed to fetch media:", mediaResponse.status);
      }
    } catch (error) {
      console.error("Error fetching media:", error);
      toast.error("Failed to load media files");
    }
  };

  // Initialize showSpecialQuestions based on existing data
  useEffect(() => {
    // Set the checkbox based on have_question field or if any special questions exist
    const hasQuestions = addon.have_question ||
      (addon.special_questions && addon.special_questions.trim() !== '') ||
      (addon.translations && Object.values(addon.translations).some(
        trans => trans.special_questions && trans.special_questions.trim() !== ''
      ));

    setShowSpecialQuestions(!!hasQuestions);
  }, [addon]);

  // Fetch existing media, price types, and pricing history on component mount
  useEffect(() => {
    const fetchData = async () => {
      let hasError = false;

      try {
        // Fetch media
        await fetchMedia();

        // Fetch price types
        const priceTypesResponse = await fetch("/api/price-types");
        if (priceTypesResponse.ok) {
          const { data } = await priceTypesResponse.json();
          setPriceTypes(data || []);
        } else {
          console.error(
            "Failed to fetch price types:",
            priceTypesResponse.status,
          );
          hasError = true;
        }

        // Fetch pricing history
        const pricingHistoryResponse = await fetch(
          `/api/venues/${venueId}/add-ons/${addon.id}/pricing-history`,
        );
        if (pricingHistoryResponse.ok) {
          const result = await pricingHistoryResponse.json();
          setPriceHistory(result.data || []);
        } else {
          console.error(
            "Failed to fetch pricing history:",
            pricingHistoryResponse.status,
          );
          hasError = true;
        }

        if (hasError) {
          toast.error(
            "Some data could not be loaded. Please refresh the page.",
          );
        }
      } catch (error) {
        console.error("Error loading add-on data:", error);
        toast.error("Failed to load add-on details. Please try again.");
      } finally {
        // Set data loading to false after all initial data is fetched
        setIsDataLoading(false);
      }
    };

    fetchData();
  }, [venueId, addon.id]);

  const handleCancel = () => {
    // Dynamically determine the correct portal based on current URL
    const currentPortal = pathname.startsWith("/booking")
      ? "booking"
      : "superadmin";
    router.push(`/${currentPortal}/venue/${venueId}/add-ons`);
  };

  const handleAddNewPrice = async () => {
    if (!newPriceData.price) return;

    setIsAddingPrice(true);
    try {
      // First, archive all currently active prices for this add-on
      const archiveResponse = await fetch(
        `/api/venues/${venueId}/add-ons/${addon.id}/archive-prices`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!archiveResponse.ok) {
      }

      // Then, create a new pricing record
      const createPricingResponse = await fetch("/api/pricings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          price: parseFloat(newPriceData.price),
          vat_percentage: parseFloat(newPriceData.vatPercentage),
          price_category: "addon",
          item_id: addon.id,
          status_id: 41, // PRICING_ACTIVE
          // Without it the price falls back to the column default, SEK.
          currency: venueCurrency,
        }),
      });

      if (!createPricingResponse.ok) {
        const error = await createPricingResponse.json();
        throw new Error(error.error || "Failed to create new pricing");
      }

      const newPricing = await createPricingResponse.json();

      // Finally, update the add-on to use the new pricing
      const response = await fetch(
        `/api/venues/${venueId}/add-ons/${addon.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            // Include required fields to satisfy API validation
            name: form.getValues("name") || addon.name,
            add_on_type: form.getValues("add_on_type") || addon.add_on_type,
            pricing_id: newPricing.id,
          }),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update add-on pricing");
      }

      // Refresh pricing history
      const pricingHistoryResponse = await fetch(
        `/api/venues/${venueId}/add-ons/${addon.id}/pricing-history`,
      );
      if (pricingHistoryResponse.ok) {
        const { data } = await pricingHistoryResponse.json();
        setPriceHistory(data || []);
      }

      setPriceModalOpen(false);
      setNewPriceData({ price: "", vatPercentage: "25" });

      // Update form values with new price
      form.setValue("price", parseFloat(newPriceData.price));
      form.setValue("vat_percentage", parseFloat(newPriceData.vatPercentage));

      // Show success message
      toast.success("Price updated successfully");

      // Refresh the page to ensure data consistency
      router.refresh();
    } catch (error: any) {
      toast.error(error.message || "Failed to update pricing");
    } finally {
      setIsAddingPrice(false);
    }
  };

  // Show loading spinner while fetching initial data
  if (isDataLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-10 w-10 animate-spin text-purple-600" />
          <p className="text-gray-600 font-medium">Loading add-on details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={handleCancel}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Edit add-on</h1>
              <p className="text-gray-600 mt-1">
                Update your add-on details and pricing
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <Form {...form}>
          <div className="bg-white rounded-lg shadow">
            <div className="p-6">
              {/* Main Tabs */}
              <div className="border-b mb-6">
                <nav className="-mb-px flex space-x-8">
                  <button
                    type="button"
                    onClick={() => setActiveTab("general")}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === "general"
                        ? "border-purple-600 text-purple-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    General settings
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("pricing")}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === "pricing"
                        ? "border-purple-600 text-purple-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    Pricing
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("content")}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === "content"
                        ? "border-purple-600 text-purple-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    Content
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("faqs")}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === "faqs"
                        ? "border-purple-600 text-purple-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    FAQs
                  </button>
                </nav>
              </div>

              {/* General Settings Tab */}
              {activeTab === "general" && (
                <div className="space-y-6">
                  <div className="border rounded-lg p-6 space-y-6">
                    <h3 className="text-lg font-semibold">Basic information</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="customer_type"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Customer type *</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select customer type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="corporate">
                                  Corporate
                                </SelectItem>
                                <SelectItem value="private">Private</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="add_on_type"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Category *</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select category" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="food">Food</SelectItem>
                                <SelectItem value="drink">Drinks</SelectItem>
                                <SelectItem value="service">
                                  Services
                                </SelectItem>
                                <SelectItem value="equipment">
                                  Equipment
                                </SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="is_on_road"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start gap-3 space-y-0 rounded-md border p-4">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>Road show add-on</FormLabel>
                            <p className="text-xs text-gray-500">
                              When enabled, this add-on is only offered on
                              road-show bookings (not on-site bookings).
                            </p>
                          </div>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="is_catered"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start gap-3 space-y-0 rounded-md border p-4">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>Catered</FormLabel>
                            <p className="text-xs text-gray-500">
                              Ordered from an external supplier (e.g.
                              canapés). Customers can only add, increase and
                              decrease it up to the cutoffs below; staff can
                              always change it.
                            </p>
                          </div>
                        </FormItem>
                      )}
                    />

                    {/* Until when customers may change this add-on. */}
                    {form.watch("is_catered") ? (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {(
                          [
                            ["catered_add_cutoff_days", "Add up to"],
                            ["catered_increase_cutoff_days", "Increase up to"],
                            ["catered_decrease_cutoff_days", "Decrease up to"],
                          ] as const
                        ).map(([name, label]) => (
                          <FormField
                            key={name}
                            control={form.control}
                            name={name}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{label}</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    min={0}
                                    {...field}
                                    value={field.value ?? ""}
                                  />
                                </FormControl>
                                <FormDescription>
                                  Days before the show
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">
                        Customers can change it up to the show&apos;s
                        participant change cutoff.
                      </p>
                    )}

                    <FormField
                      control={form.control}
                      name="price_type_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>How is this add-on priced? *</FormLabel>
                          <div className="space-y-3">
                            {priceTypes.map((priceType) => (
                              <label
                                key={priceType.id}
                                className="flex items-start p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                              >
                                <input
                                  type="radio"
                                  value={priceType.id}
                                  checked={field.value === priceType.id}
                                  onChange={() => field.onChange(priceType.id)}
                                  className="mt-1 mr-3"
                                />
                                <div>
                                  <span className="font-medium text-gray-900">
                                    {priceType.type === "fixed"
                                      ? "Fixed price"
                                      : "Per person"}
                                  </span>
                                  <p className="text-sm text-gray-600 mt-1">
                                    {priceType.type === "fixed"
                                      ? "One fixed price regardless of group size"
                                      : "Price multiplied by number of participants"}
                                  </p>
                                </div>
                              </label>
                            ))}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="order"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Sort Order</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min="0"
                                {...field}
                                onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                              />
                            </FormControl>
                            <FormDescription>
                              Lower numbers appear first (0 = default)
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="status"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Status *</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select status" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="available">
                                  Available
                                </SelectItem>
                                <SelectItem value="unavailable">
                                  Unavailable
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Set whether this add-on is currently available for
                              booking
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Accounting Article Mapping - Only for Corporate customers */}
                  {form.watch("customer_type") === "corporate" && (
                    <AccountingArticleMapping
                      venueId={venueId}
                      itemId={addon.id}
                      itemType="add-on"
                      itemName={addon.name}
                      currentArticleCode={addon.article_code || null}
                      accountingId={venueAccountingId}
                      userRoleId={userRoleId}
                    />
                  )}

                  {/* Save button for General Settings */}
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      onClick={saveGeneralSettings}
                      disabled={generalLoading}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {generalLoading && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Save General Settings
                    </Button>
                  </div>
                </div>
              )}

              {/* Pricing Tab */}
              {activeTab === "pricing" && (
                <div className="space-y-6">
                  {/* Price History Table */}
                  <div className="border rounded-lg p-6 space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-semibold">Price history</h3>
                      <Button
                        type="button"
                        onClick={() => {
                          // Reset form data when opening modal
                          setNewPriceData({
                            price: "",
                            vatPercentage: "25",
                          });
                          setPriceModalOpen(true);
                        }}
                        className="bg-purple-600 hover:bg-purple-700"
                        size="sm"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add new price
                      </Button>
                    </div>

                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Price ({getCurrencySymbol(venueCurrency)})</TableHead>
                            <TableHead>VAT %</TableHead>
                            <TableHead>Total (incl. VAT)</TableHead>
                            <TableHead>From</TableHead>
                            <TableHead>To</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Created Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {priceHistory.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={7}
                                className="text-center text-gray-500 py-8"
                              >
                                No pricing history available
                              </TableCell>
                            </TableRow>
                          ) : (
                            priceHistory.map((record) => {
                              const total =
                                record.price * (1 + record.vatPercentage / 100);
                              const getStatusBadgeClass = () => {
                                // Ensure status is a string before processing
                                if (
                                  !record.status ||
                                  typeof record.status !== "string"
                                ) {
                                  return "bg-gray-100 text-gray-800 border-gray-200";
                                }

                                const statusLower = record.status.toLowerCase();

                                // Determine badge color based on status
                                switch (statusLower) {
                                  case "active":
                                    return "bg-green-100 text-green-800 border-green-200";
                                  case "inactive":
                                  case "archived":
                                    return "bg-gray-100 text-gray-800 border-gray-200";
                                  default:
                                    return "bg-gray-100 text-gray-800 border-gray-200";
                                }
                              };

                              return (
                                <TableRow key={record.id}>
                                  <TableCell className="font-medium">
                                    {formatNumber(record.price)}
                                  </TableCell>
                                  <TableCell>{record.vatPercentage}%</TableCell>
                                  <TableCell className="font-medium">
                                    {total.toFixed(2)}
                                  </TableCell>
                                  <TableCell className="text-sm">
                                    {record.startDate}
                                  </TableCell>
                                  <TableCell className="text-sm">
                                    {record.endDate || (
                                      <span className="text-gray-500">
                                        Ongoing
                                      </span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Badge
                                      variant="secondary"
                                      className={getStatusBadgeClass()}
                                    >
                                      {record.status
                                        ? record.status
                                            .charAt(0)
                                            .toUpperCase() +
                                          record.status.slice(1)
                                        : "Unknown"}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-sm text-gray-600">
                                    {record.createdDate}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
              )}

              {/* Content Tab */}
              {activeTab === "content" && (
                <div className="space-y-6">
                  {/* Special Questions Checkbox - Common for all languages */}
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="special-questions-main"
                        checked={showSpecialQuestions}
                        onCheckedChange={(checked) => {
                          setShowSpecialQuestions(checked as boolean);
                          if (!checked) {
                            // Clear all special questions when unchecked
                            form.setValue('special_questions', '');
                            venueLanguages.forEach(lang => {
                              if (lang !== 'en') {
                                form.setValue(`translations.${lang}.special_questions`, '');
                              }
                            });
                          }
                        }}
                      />
                      <label
                        htmlFor="special-questions-main"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        Ask special questions
                      </label>
                    </div>
                    <p className="text-xs text-gray-600 mt-2 ml-6">
                      Enable this to ask customers special questions when they select this add-on. You can provide different questions for each language below.
                    </p>
                  </div>

                  {/* Language Tabs for Content */}
                  <div className="border-b">
                    <nav className="-mb-px flex space-x-8">
                      {venueLanguages.map((lang) => {
                        const language = AVAILABLE_LANGUAGES.find(
                          (l) => l.code === lang,
                        );
                        if (!language) return null;

                        return (
                          <button
                            key={lang}
                            type="button"
                            onClick={() => setActiveLanguageTab(lang)}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                              activeLanguageTab === lang
                                ? "border-purple-600 text-purple-600"
                                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                          >
                            {language.flag} {language.name}
                          </button>
                        );
                      })}
                    </nav>
                  </div>

                  <div className="border rounded-lg p-6 space-y-6">
                    <div className="space-y-4">
                      {activeLanguageTab === "en" ? (
                        <>
                          <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Name *</FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    placeholder="e.g., Birthday Cake, Pizza, Party Kit"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="description"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Description</FormLabel>
                                <FormControl>
                                  <Textarea
                                    {...field}
                                    placeholder="Describe the add-on..."
                                    rows={4}
                                  />
                                </FormControl>
                                <FormDescription>
                                  Max {MAX_ADDON_DESCRIPTION_WORDS} words.
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {showSpecialQuestions && (
                            <FormField
                              control={form.control}
                              name="special_questions"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Special Questions</FormLabel>
                                  <FormControl>
                                    <RichTextEditor
                                      value={field.value || ""}
                                      onChange={field.onChange}
                                      placeholder="Enter questions that customers need to answer when selecting this add-on... (supports HTML and links)"
                                    />
                                  </FormControl>
                                  <FormDescription>
                                    Customers will receive an email with these questions when they add this add-on to their booking. Supports HTML formatting and links.
                                  </FormDescription>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}
                        </>
                      ) : (
                        <>
                          <FormItem>
                            <FormLabel>Name</FormLabel>
                            <FormControl>
                              <Input
                                value={
                                  form.watch(
                                    `translations.${activeLanguageTab}.name`,
                                  ) || ""
                                }
                                onChange={(e) =>
                                  form.setValue(
                                    `translations.${activeLanguageTab}.name`,
                                    e.target.value,
                                  )
                                }
                                placeholder={`Name in ${AVAILABLE_LANGUAGES.find((l) => l.code === activeLanguageTab)?.name}`}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>

                          <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                              <Textarea
                                value={
                                  form.watch(
                                    `translations.${activeLanguageTab}.description`,
                                  ) || ""
                                }
                                onChange={(e) =>
                                  form.setValue(
                                    `translations.${activeLanguageTab}.description`,
                                    e.target.value,
                                  )
                                }
                                placeholder={`Description in ${AVAILABLE_LANGUAGES.find((l) => l.code === activeLanguageTab)?.name}`}
                                rows={4}
                              />
                            </FormControl>
                            <FormDescription>
                              Max {MAX_ADDON_DESCRIPTION_WORDS} words.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>

                          {showSpecialQuestions && (
                            <FormItem>
                              <FormLabel>Special Questions</FormLabel>
                              <FormControl>
                                <RichTextEditor
                                  value={
                                    form.watch(
                                      `translations.${activeLanguageTab}.special_questions`,
                                    ) || ""
                                  }
                                  onChange={(value) =>
                                    form.setValue(
                                      `translations.${activeLanguageTab}.special_questions`,
                                      value,
                                    )
                                  }
                                  placeholder={`Special questions in ${AVAILABLE_LANGUAGES.find((l) => l.code === activeLanguageTab)?.name} (supports HTML and links)`}
                                />
                              </FormControl>
                              <FormDescription>
                                Customers will receive an email with these questions when they add this add-on to their booking. Supports HTML formatting and links.
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Save button for Name & Description */}
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      onClick={saveContent}
                      disabled={contentLoading}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {contentLoading && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Save Changes
                    </Button>
                  </div>

                  {/* Media Upload Section */}
                  <div className="border rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4">
                      Media (
                      {
                        AVAILABLE_LANGUAGES.find(
                          (l) => l.code === activeLanguageTab,
                        )?.name
                      }
                      )
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Upload an image for this language. Image is saved
                      automatically.
                    </p>
                    <AddOnMediaUpload
                      media={mediaItems}
                      onChange={setMediaItems}
                      language={activeLanguageTab}
                      venueId={venueId}
                      addOnId={addon.id}
                    />
                  </div>
                </div>
              )}

              {/* FAQs Tab */}
              {activeTab === "faqs" && (
                <ItemFaqsTab
                  kind="add_on"
                  itemId={addon.id}
                  itemName={addon.name}
                  venueId={venueId}
                />
              )}
            </div>
          </div>
        </Form>
      </div>

      {/* Add New Price Modal */}
      <Dialog open={priceModalOpen} onOpenChange={setPriceModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add new price</DialogTitle>
            <DialogDescription>
              Set a new price for this add-on. The new price will be active
              immediately.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-price">Price ({getCurrencySymbol(venueCurrency)}) *</Label>
                <Input
                  id="new-price"
                  type="number"
                  value={newPriceData.price}
                  onChange={(e) => {
                    // Prevent negative values
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value) && value >= 0) {
                      setNewPriceData((prev) => ({
                        ...prev,
                        price: e.target.value,
                      }));
                    } else if (e.target.value === "") {
                      // Allow empty string for clearing
                      setNewPriceData((prev) => ({
                        ...prev,
                        price: "",
                      }));
                    }
                  }}
                  onKeyDown={(e) => {
                    // Prevent minus key
                    if (e.key === "-" || e.key === "e") {
                      e.preventDefault();
                    }
                  }}
                  placeholder="0"
                  min="0"
                  step="1"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-vat">VAT percentage (%)</Label>
                <Input
                  id="new-vat"
                  type="number"
                  value={newPriceData.vatPercentage}
                  onChange={(e) => {
                    // Prevent negative values
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value) && value >= 0 && value <= 100) {
                      setNewPriceData((prev) => ({
                        ...prev,
                        vatPercentage: e.target.value,
                      }));
                    } else if (e.target.value === "") {
                      // Allow empty string for clearing
                      setNewPriceData((prev) => ({
                        ...prev,
                        vatPercentage: "",
                      }));
                    }
                  }}
                  onKeyDown={(e) => {
                    // Prevent minus key
                    if (e.key === "-" || e.key === "e") {
                      e.preventDefault();
                    }
                  }}
                  placeholder="25"
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>
            </div>

            {/* Price Preview */}
            {newPriceData.price && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-3">Price preview</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Base price:</span>
                    <span className="font-medium">
                      {newPriceData.price} {getCurrencySymbol(venueCurrency)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>VAT ({newPriceData.vatPercentage}%):</span>
                    <span className="font-medium">
                      +
                      {(
                        (parseFloat(newPriceData.price) *
                          parseFloat(newPriceData.vatPercentage)) /
                        100
                      ).toFixed(2)}{" "}
                      {getCurrencySymbol(venueCurrency)}
                    </span>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between font-semibold">
                      <span>Total (incl. VAT):</span>
                      <span className="text-purple-600">
                        {(
                          parseFloat(newPriceData.price) *
                          (1 + parseFloat(newPriceData.vatPercentage) / 100)
                        ).toFixed(2)}{" "}
                        {getCurrencySymbol(venueCurrency)}
                      </span>
                    </div>
                  </div>
                  {form.watch("price_type_id") === 2 && ( // 2 is 'per_person' price type
                    <div className="mt-3 p-2 bg-blue-50 rounded text-blue-800 text-xs">
                      <strong>Note:</strong> This price will be multiplied by
                      the number of participants
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPriceModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddNewPrice}
              disabled={!newPriceData.price || isAddingPrice}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isAddingPrice ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                "Add Price"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
